import { useId, useState } from 'react';
import { useSearchDishes } from '@/api/dishes/dishes';
import { Input } from '@/components/ui/field';
import { kcal } from '@/lib/utils';

/** The API rejects a shorter query, so there is no point sending one. */
const MIN_QUERY = 2;

export interface DishPick {
  dishId: string;
  qty: number;
}

/**
 * Dish autocomplete plus a serving count.
 *
 * Picking a row hands the caller a dish id and a quantity; what happens to it —
 * logged as eaten, or added to the plan — is the caller's business. Shared so
 * the two screens cannot drift into searching or pricing food differently.
 */
export function DishPicker({
  onPick,
  busy = false,
  placeholder = 'ค้นหาอาหาร เช่น ข้าวผัด',
}: {
  onPick: (pick: DishPick) => void;
  busy?: boolean;
  placeholder?: string;
}) {
  const qtyId = useId();
  const [query, setQuery] = useState('');
  const [qty, setQty] = useState('1');

  const dishes = useSearchDishes(
    { q: query },
    { query: { enabled: query.trim().length >= MIN_QUERY } },
  );

  const servings = Number(qty);
  const validQty = Number.isFinite(servings) && servings > 0;

  return (
    <div className="space-y-3">
      <Input
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={placeholder}
        aria-label="ค้นหาอาหาร"
      />

      <div className="flex items-center gap-2">
        <label className="text-sm text-ink-muted" htmlFor={qtyId}>
          จำนวน
        </label>
        <Input
          id={qtyId}
          type="number"
          min="0.1"
          step="0.5"
          value={qty}
          onChange={(event) => setQty(event.target.value)}
          className="w-24"
        />
      </div>

      {dishes.isPending && query.trim().length >= MIN_QUERY && (
        <p className="text-sm text-ink-muted">กำลังค้นหา…</p>
      )}

      {dishes.data?.length === 0 && <p className="text-sm text-ink-muted">ไม่พบอาหารนี้ในระบบ</p>}

      <ul className="divide-y divide-border-subtle">
        {dishes.data?.map((dish) => (
          <li key={dish.id}>
            <button
              type="button"
              disabled={!validQty || busy}
              onClick={() => onPick({ dishId: dish.id, qty: servings })}
              className="flex w-full items-center gap-3 py-2.5 text-left disabled:opacity-50"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{dish.nameTh}</span>
                <span className="block text-xs text-ink-muted">
                  {dish.servingLabel}
                  {!dish.verified && ' · ประมาณการ'}
                </span>
              </span>
              <span className="shrink-0 text-sm tabular-nums">
                {kcal(dish.kcalPerServing * (validQty ? servings : 1))} kcal
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
